import React, { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, RefreshControl, View } from 'react-native';
import { useQuery } from '@apollo/client';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import moment from 'moment';
import { Feather } from '@expo/vector-icons';

import {
	PEOPLE_DOCUMENT_ASSIGNMENT_STATUS,
	PEOPLE_DOCUMENT_ASSIGNMENT_STATUS_NAMES,
} from '../../../common/constants';
import {
	GET_COMPANY_DOCUMENT_ASSIGNMENTS,
} from '../../../common/graphql/people/companyDocumentAssignment';
import {
	GET_EMPLOYEE_DOCUMENT_ASSIGNMENTS,
} from '../../../common/graphql/people/employeeDocumentAssignment';
import {
	GET_EMPLOYEE_DOCUMENT_TYPES_MOBILE,
	GET_MY_EMPLOYEE_PROFILE_MOBILE,
	GET_MY_EMPLOYEE_DOCUMENT_RECORDS_MOBILE,
} from '../../graphql/people';
import { Picker, Text } from '../../components';
import { TextInput } from '../../components/input';
import {
	Card,
	EmptyState,
	Screen,
	SecondaryButton,
	SectionTitle,
	Tag,
} from '../../design/components';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import MyDocumentUploadForm from './MyDocumentUploadForm';
import { createMyDocumentsStyles } from './MyDocumentsView.styles';
import useMyDocumentUpload, { NEW_DOCUMENT_UPLOAD_ID } from './useMyDocumentUpload';

const ACTIONABLE_STATUSES = [
	PEOPLE_DOCUMENT_ASSIGNMENT_STATUS.REQUIRED,
	PEOPLE_DOCUMENT_ASSIGNMENT_STATUS.OVERDUE,
	PEOPLE_DOCUMENT_ASSIGNMENT_STATUS.OPTIONAL,
];

const OTHER_DOCUMENT_TYPE_ID = 'other-document';

const isActionableStatus = status => ACTIONABLE_STATUSES.includes(Number(status));

const resolveStatusTone = (status) => {
	switch (Number(status)) {
		case PEOPLE_DOCUMENT_ASSIGNMENT_STATUS.COMPLETE:
			return 'success';
		case PEOPLE_DOCUMENT_ASSIGNMENT_STATUS.OVERDUE:
			return 'danger';
		case PEOPLE_DOCUMENT_ASSIGNMENT_STATUS.WAIVED:
			return 'neutral';
		default:
			return 'warning';
	}
};

const resolveCompanyAssignmentTitle = assignment => (
	String(assignment?.issuedTitle || '').trim() ||
	assignment?.companyDocument?.title ||
	'Company document'
);

const formatDueDate = dueAt => (dueAt ? `Due ${moment(dueAt).format('D MMM YYYY')}` : '');

// Self-service documents for the signed-in employee: company documents to read and
// acknowledge, plus requested document uploads (licences, certificates) from a photo or file.
const MyDocumentsView = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createMyDocumentsStyles);
	const { theme: { ui: { colors } } } = useTheme();

	const [isRefreshing, setIsRefreshing] = useState(false);

	const { data: profileData, loading: profileLoading } = useQuery(GET_MY_EMPLOYEE_PROFILE_MOBILE, {
		fetchPolicy: 'cache-and-network',
	});
	const employee = profileData?.getMyEmployeeProfile || null;

	const {
		data: companyAssignmentData,
		loading: companyLoading,
		refetch: refetchCompanyAssignments,
	} = useQuery(GET_COMPANY_DOCUMENT_ASSIGNMENTS, {
		variables: {
			page: 0,
			perPage: 100,
			sortBy: 'dueAt',
			sortDirection: 1,
			filter: {},
		},
		fetchPolicy: 'cache-and-network',
	});

	const {
		data: employeeAssignmentData,
		loading: employeeLoading,
		refetch: refetchEmployeeAssignments,
	} = useQuery(GET_EMPLOYEE_DOCUMENT_ASSIGNMENTS, {
		variables: {
			page: 0,
			perPage: 100,
			sortBy: 'dueAt',
			sortDirection: 1,
			filter: {},
		},
		fetchPolicy: 'cache-and-network',
	});

	const {
		data: documentTypeData,
		loading: documentTypesLoading,
	} = useQuery(GET_EMPLOYEE_DOCUMENT_TYPES_MOBILE, {
		skip: !employee?.id,
		variables: {
			page: 0,
			perPage: 500,
			sortBy: 'name',
			sortDirection: 1,
		},
		fetchPolicy: 'cache-and-network',
	});

	const {
		data: employeeDocumentRecordData,
		loading: employeeDocumentRecordsLoading,
		refetch: refetchEmployeeDocumentRecords,
	} = useQuery(GET_MY_EMPLOYEE_DOCUMENT_RECORDS_MOBILE, {
		skip: !employee?.id,
		variables: {
			page: 0,
			perPage: 100,
			sortBy: 'created',
			sortDirection: -1,
		},
		fetchPolicy: 'cache-and-network',
	});

	const companyAssignments = useMemo(() => (
		companyAssignmentData?.getCompanyDocumentAssignments?.items || []
	), [companyAssignmentData?.getCompanyDocumentAssignments?.items]);

	const employeeAssignments = useMemo(() => (
		employeeAssignmentData?.getEmployeeDocumentAssignments?.items || []
	), [employeeAssignmentData?.getEmployeeDocumentAssignments?.items]);

	const documentTypes = useMemo(() => (
		documentTypeData?.getEmployeeDocuments?.items || []
	), [documentTypeData?.getEmployeeDocuments?.items]);

	const documentTypeOptions = useMemo(() => ([
		...documentTypes.map(documentType => ({
			label: documentType.name || 'Document type',
			value: documentType.id,
		})),
		{ label: 'Other document', value: OTHER_DOCUMENT_TYPE_ID },
	]), [documentTypes]);

	const employeeDocumentRecords = useMemo(() => (
		employeeDocumentRecordData?.getEmployeeDocumentRecords?.items || []
	), [employeeDocumentRecordData?.getEmployeeDocumentRecords?.items]);

	const refetchAll = useCallback(async () => {
		await Promise.all([
			refetchCompanyAssignments(),
			refetchEmployeeAssignments(),
			...(employee?.id ? [refetchEmployeeDocumentRecords()] : []),
		]);
	}, [employee?.id, refetchCompanyAssignments, refetchEmployeeAssignments, refetchEmployeeDocumentRecords]);

	useFocusEffect(useCallback(() => {
		refetchAll().catch(() => {});
	}, [refetchAll]));

	const refresh = useCallback(async () => {
		setIsRefreshing(true);
		try {
			await refetchAll();
		}
		finally {
			setIsRefreshing(false);
		}
	}, [refetchAll]);

	const openCompanyAssignment = useCallback((assignment) => {
		navigation.navigate('MyDocumentViewerView', { assignment });
	}, [navigation]);

	const upload = useMyDocumentUpload({ employeeId: employee?.id, refetchAll });
	const {
		uploadAssignmentId,
		selectedDocumentTypeId,
		documentCategory,
		setDocumentCategory,
		isSubmitting,
		startUpload,
		startNewUpload,
		cancelUpload,
		changeDocumentType,
	} = upload;

	const selectedDocumentType = useMemo(() => (
		documentTypes.find(documentType => documentType.id === selectedDocumentTypeId) || null
	), [documentTypes, selectedDocumentTypeId]);

	const loading = (profileLoading && !employee) ||
		(companyLoading && !companyAssignments.length) ||
		(employeeLoading && !employeeAssignments.length) ||
		(employeeDocumentRecordsLoading && !employeeDocumentRecords.length);

	if (!profileLoading && !employee) {
		return (
			<Screen backLabel="My account" heading="Documents">
				<EmptyState
					icon="user-x"
					title="Employee profile required"
					message="Documents are available once an employee profile is linked to this account."
				/>
			</Screen>
		);
	}

	return (
		<Screen
			backLabel="My account"
			heading="Documents"
			subheading="Company documents assigned to you and documents saved to your employee profile."
			refreshControl={(
				<RefreshControl
					refreshing={isRefreshing}
					onRefresh={refresh}
					tintColor={colors.accent}
				/>
			)}
		>
			{
				loading ? (
					<View style={styles.inlineLoading} accessibilityLabel="Loading">
						<ActivityIndicator color={colors.accent} />
					</View>
				) : null
			}

			<View style={styles.section}>
				<SectionTitle title="Company documents" />
				{
					!companyLoading && !companyAssignments.length ? (
						<Text style={styles.mutedText}>No company documents are assigned to you.</Text>
					) : null
				}
				{companyAssignments.map((assignment) => {
					const actionable = isActionableStatus(assignment.status);
					const title = resolveCompanyAssignmentTitle(assignment);
					const meta = `${assignment.companyDocument?.categoryLabel || 'Document'}${actionable && assignment.dueAt ? ` · ${formatDueDate(assignment.dueAt)}` : ''}`;
					const statusLabel = PEOPLE_DOCUMENT_ASSIGNMENT_STATUS_NAMES[assignment.status] || 'Required';

					return (
						<Card
							key={assignment.id}
							tone="raised"
							onPress={() => openCompanyAssignment(assignment)}
							accessibilityLabel={[title, meta, statusLabel].join(', ')}
						>
							<View style={styles.itemRow}>
								<View style={styles.itemContent}>
									<Text style={styles.rowTitle}>{title}</Text>
									<Text style={styles.rowMeta}>{meta}</Text>
									<Tag
										label={statusLabel}
										tone={resolveStatusTone(assignment.status)}
										style={styles.statusTag}
									/>
								</View>
								<Feather name="chevron-right" size={22} color={colors.iconMuted} />
							</View>
						</Card>
					);
				})}
			</View>

			<View style={styles.section}>
				<SectionTitle title="My documents" />
				<Text style={styles.mutedText}>
					Upload a new document at any time, or complete a document request below.
				</Text>
				{
					uploadAssignmentId !== NEW_DOCUMENT_UPLOAD_ID ? (
						<SecondaryButton
							label="Upload new document"
							icon="upload"
							onPress={startNewUpload}
							disabled={Boolean(uploadAssignmentId)}
							style={styles.uploadButton}
						/>
					) : (
						<Card tone="raised">
							<Text style={styles.formTitle}>New document</Text>
							<View style={styles.formField}>
								<Text style={styles.formLabel}>Document type</Text>
								<Picker
									options={documentTypeOptions}
									selectedValue={selectedDocumentTypeId}
									onValueChange={changeDocumentType}
									emptyValueLabel={documentTypesLoading ? 'Loading document types…' : 'Select document type'}
									disabled={documentTypesLoading}
									width={240}
								/>
							</View>
							{selectedDocumentTypeId === OTHER_DOCUMENT_TYPE_ID ? (
								<View style={styles.formField}>
									<Text style={styles.formLabel}>Document name</Text>
									<TextInput
										value={documentCategory}
										onChange={setDocumentCategory}
										placeholder="e.g. first aid certificate"
										disabled={isSubmitting}
									/>
								</View>
							) : null}
							{selectedDocumentTypeId ? (
								<MyDocumentUploadForm
									upload={upload}
									documentType={selectedDocumentType}
									category={documentCategory}
								/>
							) : (
								<SecondaryButton label="Cancel" onPress={cancelUpload} style={styles.uploadButton} />
							)}
						</Card>
					)
				}
			</View>

			<View style={styles.section}>
				<SectionTitle title="Uploaded documents" />
				{
					!employeeDocumentRecordsLoading && !employeeDocumentRecords.length ? (
						<Text style={styles.mutedText}>No documents uploaded yet.</Text>
					) : null
				}
				{employeeDocumentRecords.map(document => (
					<Card key={document.id}>
						<Text style={styles.rowTitle}>
							{document.title || document.documentType?.name || document.category || 'Document'}
						</Text>
						<Text style={styles.rowMeta}>
							{document.created ? `Uploaded ${moment(document.created).format('D MMM YYYY')}` : 'Uploaded document'}
							{document.expiresAt ? ` · Expires ${moment(document.expiresAt).format('D MMM YYYY')}` : ''}
						</Text>
					</Card>
				))}
			</View>

			<View style={styles.section}>
				<SectionTitle title="Requested documents" />
				{
					!employeeLoading && !employeeAssignments.length ? (
						<Text style={styles.mutedText}>Nothing is requested from you right now.</Text>
					) : null
				}
				{employeeAssignments.map((assignment) => {
					const actionable = isActionableStatus(assignment.status);
					const isUploading = uploadAssignmentId === assignment.id;

					return (
						<Card key={assignment.id} tone={actionable ? 'raised' : 'default'}>
							<Text style={styles.rowTitle}>{assignment.documentType?.name || 'Document'}</Text>
							{
								actionable && assignment.dueAt ? (
									<Text style={styles.rowMeta}>{formatDueDate(assignment.dueAt)}</Text>
								) : null
							}
							<Tag
								label={PEOPLE_DOCUMENT_ASSIGNMENT_STATUS_NAMES[assignment.status] || 'Required'}
								tone={resolveStatusTone(assignment.status)}
								style={styles.statusTag}
							/>
							{
								actionable && !isUploading ? (
									<SecondaryButton
										label="Upload document"
										icon="upload"
										onPress={() => startUpload(assignment)}
										style={styles.uploadButton}
									/>
								) : null
							}
							{isUploading ? (
								<MyDocumentUploadForm
									upload={upload}
									assignment={assignment}
									documentType={assignment.documentType}
								/>
							) : null}
						</Card>
					);
				})}
			</View>
		</Screen>
	);
};

export default MyDocumentsView;
